import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader,
  Container, Badge, Button, Table,
} from "reactstrap";
import { adminPaths } from "admin/paths";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { bookingsRepo } from "data/repositories/bookingsRepo";
import { businessesRepo } from "data/repositories/businessesRepo";
import { usersRepo } from "data/repositories/usersRepo";
import { sortByOption, toDateLabel } from "admin/contentUtils";
import { bookingKindLabels, travelProviderLabels } from "data/admin/domain";

const KIND_ICON = { stay: "fas fa-bed", flight: "fas fa-plane", hotel: "fas fa-hotel", car: "fas fa-car" };
const STATUS_COLOR = { pending: "warning", confirmed: "success", cancelled: "danger" };
const STATUS_LABEL = { pending: "Bekliyor", confirmed: "Onaylandı", cancelled: "İptal" };
const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "start-asc", label: "Başlangıç tarihi" },
  { value: "customer-asc", label: "Müşteri adına göre" },
];

export default function AdminBookings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [sortBy, setSortBy] = useState("updated-desc");

  const businessMap = useMemo(() => {
    const map = new Map();
    businessesRepo.list({ includeDeleted: true }).forEach((item) => map.set(item.id, item));
    return map;
  }, []);

  const userMap = useMemo(() => {
    const map = new Map();
    usersRepo.list({ includeDeleted: true }).forEach((item) => map.set(item.uid || item.id, item));
    return map;
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setBookings(bookingsRepo.list());
    } catch (loadError) {
      setError(loadError?.message || "Rezervasyonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = bookings.filter((booking) => {
      if (statusFilter && booking.status !== statusFilter) return false;
      if (kindFilter && (booking.kind || "stay") !== kindFilter) return false;
      if (!query) return true;

      const business = businessMap.get(booking.businessId);
      const user = userMap.get(booking.customerUserId);

      return [
        booking.id,
        booking.businessId,
        booking.travel?.title,
        booking.travel?.subtitle,
        travelProviderLabels[booking.travel?.provider],
        booking.customerUserId,
        booking.customerDisplayName,
        booking.customerName,
        business?.name,
        user?.displayName,
        user?.email,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return sortByOption(next, sortBy, {
      "start-asc": (left, right) => String(left.startDate || "").localeCompare(String(right.startDate || "")),
      "customer-asc": (left, right) =>
        String(left.customerDisplayName || left.customerName || "").localeCompare(String(right.customerDisplayName || right.customerName || ""), "tr"),
    });
  }, [bookings, businessMap, kindFilter, search, sortBy, statusFilter, userMap]);

  const handleStatus = (id, status) => {
    try {
      bookingsRepo.updateStatus(id, status);
      setFlash({ color: "success", message: `Rezervasyon durumu ${STATUS_LABEL[status] || status} olarak güncellendi.` });
      load();
    } catch (updateError) {
      setFlash({ color: "danger", message: updateError?.message || "Rezervasyon güncellenemedi." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setKindFilter("");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        title="Yönetim — Rezervasyonlar"
        description="İşletme rezervasyonları ile portal ve mobilde uçuş, otel ve araç kiralama aramalarından başlatılan seyahat rezervasyonları."
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Tüm Rezervasyonlar</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Rezervasyon, işletme, seyahat veya kullanıcı ara"
              filters={[
                {
                  key: "kind",
                  label: "Tür",
                  value: kindFilter,
                  onChange: setKindFilter,
                  options: [{ value: "", label: "Tüm türler" }, ...Object.keys(bookingKindLabels).map((kind) => ({ value: kind, label: bookingKindLabels[kind] }))],
                },
                {
                  key: "status",
                  label: "Durum",
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [{ value: "", label: "Tüm durumlar" }, ...Object.keys(STATUS_LABEL).map((status) => ({ value: status, label: STATUS_LABEL[status] }))],
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={bookings.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              flash={flash}
              onDismissFlash={() => setFlash(null)}
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={filtered.length === 0}
              emptyTitle="Henüz rezervasyon yok"
            >
              <Table className="align-items-center" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Rezervasyon</th>
                    <th>İşletme / Seyahat</th>
                    <th>Kullanıcı</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th className="text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => {
                    const business = businessMap.get(b.businessId);
                    const user = userMap.get(b.customerUserId);
                    const kind = b.kind || "stay";
                    const travel = b.travel;
                    return (
                    <tr key={b.id}>
                      <td>
                        <div className="font-weight-bold">{b.customerDisplayName || b.customerName || "Rezervasyon"}</div>
                        <div className="small text-muted">
                          <i className={`${KIND_ICON[kind]} mr-1`} />
                          {bookingKindLabels[kind]}
                        </div>
                      </td>
                      <td>
                        {travel ? (
                          <>
                            <div>{travel.title}</div>
                            <div className="small text-muted">
                              {[travelProviderLabels[travel.provider] || travel.provider, travel.priceText].filter(Boolean).join(" · ")}
                            </div>
                          </>
                        ) : (
                          <>
                            <div>{business?.name || b.businessId}</div>
                            <div className="small text-muted">{b.businessId}</div>
                          </>
                        )}
                      </td>
                      <td>
                        <div>{user?.displayName || b.customerDisplayName || b.customerName || "-"}</div>
                        <div className="small text-muted">{user?.email || b.customerUserId || "-"}</div>
                      </td>
                      <td>
                        <div>
                          {toDateLabel(b.startDate)}
                          {travel?.endDate ? ` – ${toDateLabel(travel.endDate)}` : ""}
                        </div>
                        <div className="small text-muted">
                          {travel
                            ? travel.travelers
                              ? `${travel.travelers} ${kind === "hotel" ? "misafir" : "yolcu"}`
                              : "-"
                            : `${b.nights || 1} gece`}
                        </div>
                      </td>
                      <td>
                        <Badge color={STATUS_COLOR[b.status] || "secondary"} pill>
                          {STATUS_LABEL[b.status] || b.status}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <Button color="primary" size="sm" className="mr-2" tag={Link} to={`/admin${adminPaths.admin.bookingDetail(b.id)}`}>
                          Detay
                        </Button>
                        {b.status === "pending" && (
                          <>
                            <Button color="success" size="sm" className="mr-1" onClick={() => handleStatus(b.id, "confirmed")}>
                              Onayla
                            </Button>
                            <Button color="danger" size="sm" onClick={() => handleStatus(b.id, "cancelled")}>
                              Reddet
                            </Button>
                          </>
                        )}
                        {b.status === "confirmed" && (
                          <Button color="danger" size="sm" onClick={() => handleStatus(b.id, "cancelled")}>
                            İptal Et
                          </Button>
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
